import React, { useEffect, useState } from "react";
import { Button, InputNumber, message } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_MODERATION_SETTINGS,
  UPDATE_MODERATION_SETTINGS,
} from "../../api/graphql/moderation";
import styles from "./styles.module.scss";

type Settings = { autoApproveAfterMinutes: number };

/** How long an order on manual moderation waits for an admin. */
const ModerationSettingsForm: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{
    getModerationSettings: Settings;
  }>(GET_MODERATION_SETTINGS, { fetchPolicy: "network-only" });
  const [save, { loading: saving }] = useMutation(UPDATE_MODERATION_SETTINGS);
  const saved = data?.getModerationSettings?.autoApproveAfterMinutes;
  const [minutes, setMinutes] = useState<number | null>(null);

  useEffect(() => {
    if (saved != null) setMinutes(saved);
  }, [saved]);

  if (error) return <p>The moderation settings could not be loaded.</p>;
  if (loading || minutes === null || saved == null) return <p>Loading…</p>;

  const valid = Number.isInteger(minutes) && minutes >= 1 && minutes <= 1440;

  const handleSave = async () => {
    try {
      await save({
        variables: { settings: { autoApproveAfterMinutes: minutes } },
      });
      await refetch();
      message.success("Moderation settings saved");
    } catch (e: any) {
      message.error(e?.message || "The settings were not saved");
    }
  };

  return (
    <div className={styles.settings}>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="moderation-wait">
          Approve automatically after
        </label>
        <InputNumber
          id="moderation-wait"
          min={1}
          max={1440}
          precision={0}
          value={minutes}
          onChange={(value) => setMinutes(Number(value ?? 0))}
          addonAfter="min"
          className={styles.delayInput}
        />
      </div>
      <Button
        type="primary"
        onClick={handleSave}
        loading={saving}
        disabled={minutes === saved || !valid}
        className={styles.saveButton}
      >
        Save
      </Button>
    </div>
  );
};

export default ModerationSettingsForm;
